import React, { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import moment from "moment";
import { Link } from "react-router-dom";
import TaskSkeleton from "./TaskSkeleton";

export default function DueReport() {
    const { http, user } = AuthUser();
    const [tasks, setTasks] = useState([]);
    const [date, setDate] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [remark, setRemark] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [currentStatus, setCurrentStatus] = useState(null);
    const [loginUser, setLoginUser] = useState(user.department_id);
    const [editColumn, setEditColumn] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editUser, setEditUser] = useState({});
    const [showCancelled, setShowCancelled] = useState(false);
    const [showOnlyCancelled, setShowOnlyCancelled] = useState(false);
    const [selectedYears, setSelectedYears] = useState([]);
    const [availableYears, setAvailableYears] = useState([]);

    const getCurrentAcademicYear = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1;
        if (month >= 4) {
            return `${year}-${String(year + 1).slice(-2)}`;
        }
        return `${year - 1}-${String(year).slice(-2)}`;
    };
    
    useEffect(() => {
        fetchTaskDetails();
    }, []);

    const fetchTaskDetails = () => {
        setIsLoading(true);
        let endpoint = `/due_report?company_id=${user.company_id}`;

        http
            .get(endpoint)
            .then((res) => {
                setIsLoading(false);
                if (Array.isArray(res.data.data)) {
                    setTasks(res.data.data);
                    const years = [...new Set(res.data.data.map(t => t.year || 'Unknown'))]
                        .sort((a, b) => {
                            const ya = parseInt(a.split('-')[0]);
                            const yb = parseInt(b.split('-')[0]);
                            return yb - ya;
                        });
                    setAvailableYears(years);
                    const currentAY = getCurrentAcademicYear();
                    if (years.includes(currentAY)) {
                        setSelectedYears([currentAY]);
                    } else if (years.length > 0) {
                        setSelectedYears([years[0]]);
                    }
                } else {
                    setIsLoading(false);
                    console.error("API response is not an array");
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error("Error fetching tasks:", error);
            });
    };

    // Group tasks by year and sort
    const groupAndSortByYear = (data) => {
        const grouped = {};
        data.forEach(task => {
            const year = task.year || 'Unknown';
            if (!grouped[year]) {
                grouped[year] = [];
            }
            grouped[year].push(task);
        });
        
        // Sort years in descending order (newest first)
        const sortedYears = Object.keys(grouped).sort((a, b) => {
            const yearA = parseInt(a.split('-')[0]);
            const yearB = parseInt(b.split('-')[0]);
            return yearB - yearA;
        });
        
        return { grouped, sortedYears };
    };

    const toggleYear = (year) => {
        setSelectedYears(prev => {
            if (prev.includes(year)) {
                if (prev.length === 1) return prev;
                return prev.filter(y => y !== year);
            }
            return [...prev, year];
        });
    };

    const selectAllYears = () => {
        setSelectedYears([...availableYears]);
    };

    const isAllYearsSelected =
        availableYears.length > 0 &&
        availableYears.every((y) => selectedYears.includes(y));

    // Filter by selected years first, then cancelled classes
    const yearFilteredTasks = tasks.filter(task => selectedYears.includes(task.year || 'Unknown'));

    const filteredTasks = showOnlyCancelled 
        ? yearFilteredTasks.filter(task => {
            const classUpper = (task.class || '').toUpperCase();
            const devUpper = (task.dev || '').toUpperCase();
            return classUpper.includes('CANCELLED') || devUpper.includes('CANCELLED') ||
                   classUpper.includes('Z CANCELLED') || devUpper.includes('Z CANCELLED');
        })
        : showCancelled 
            ? yearFilteredTasks 
            : yearFilteredTasks.filter(task => {
                const classUpper = (task.class || '').toUpperCase();
                const devUpper = (task.dev || '').toUpperCase();
                return !classUpper.includes('CANCELLED') && !devUpper.includes('CANCELLED') &&
                       !classUpper.includes('Z CANCELLED') && !devUpper.includes('Z CANCELLED');
            });

    const { grouped, sortedYears } = groupAndSortByYear(filteredTasks);
    
    // Calculate totals
    const totalAllStudents = filteredTasks.reduce((sum, task) => sum + (task.all_students || 0), 0);
    const totalStudents = filteredTasks.reduce((sum, task) => sum + (task.total_students || 0), 0);
    const totalDue = filteredTasks.reduce((sum, task) => sum + (task.total_due || 0), 0);
    const totalPaid = filteredTasks.reduce((sum, task) => sum + (task.total_ext_6 || 0), 0);
    const totalMaster = filteredTasks.reduce((sum, task) => sum + (task.total_master || 0), 0);

    return (
        <>
            <Toaster />

            <div className="container mt-1">
                <div className="row mb-3">
                    <div className="col-md-6">
                        <h3>Class Due Report</h3>
                    </div>
                    <div className="col-md-6 text-end">
                        <div className="form-check form-switch d-inline-block me-4">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                role="switch" 
                                id="showCancelledToggle"
                                checked={showCancelled}
                                onChange={(e) => setShowCancelled(e.target.checked)}
                                disabled={showOnlyCancelled}
                                style={{ width: '50px', height: '25px', cursor: 'pointer' }}
                            />
                            <label 
                                className="form-check-label ms-2" 
                                htmlFor="showCancelledToggle"
                                style={{ fontSize: '16px', fontWeight: '500', cursor: 'pointer' }}
                            >
                                Show Cancelled Classes
                            </label>
                        </div>
                        <div className="form-check form-switch d-inline-block">
                            <input 
                                className="form-check-input" 
                                type="checkbox" 
                                role="switch" 
                                id="showOnlyCancelledToggle"
                                checked={showOnlyCancelled}
                                onChange={(e) => setShowOnlyCancelled(e.target.checked)}
                                style={{ width: '50px', height: '25px', cursor: 'pointer', backgroundColor: showOnlyCancelled ? '#dc3545' : '' }}
                            />
                            <label 
                                className="form-check-label ms-2" 
                                htmlFor="showOnlyCancelledToggle"
                                style={{ fontSize: '16px', fontWeight: '500', cursor: 'pointer', color: showOnlyCancelled ? '#dc3545' : '' }}
                            >
                                Only Cancelled
                            </label>
                        </div>
                    </div>
                </div>

                {availableYears.length > 0 && (
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className="d-flex align-items-center flex-wrap gap-2">
                                <span style={{ fontWeight: 600, fontSize: '14px', color: '#555', marginRight: '4px' }}>Academic Year:</span>
                                {availableYears.map(year => (
                                    <label
                                        key={year}
                                        className={`btn btn-sm ${selectedYears.includes(year) ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        style={{ cursor: 'pointer', borderRadius: '20px', fontSize: '13px', fontWeight: 500, padding: '4px 14px' }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedYears.includes(year)}
                                            onChange={() => toggleYear(year)}
                                            style={{ display: 'none' }}
                                        />
                                        {year}
                                    </label>
                                ))}
                                <button
                                    type="button"
                                    className={`btn btn-sm ${isAllYearsSelected ? 'btn-dark' : 'btn-outline-dark'}`}
                                    style={{ borderRadius: '20px', fontSize: '13px', fontWeight: 500, padding: '4px 14px' }}
                                    onClick={selectAllYears}
                                >
                                    All
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="table-responsive">
                    {isLoading ? (
                        <TaskSkeleton />
                    ) : (
                        <>
                            <table className="table table-striped">
                                <thead className="table-primary">
                                    <tr>
                                        <th className="text-danger">No</th>
                                        <th>Class</th>
                                        <th>Division</th>
                                        <th>All Students <br /><b>({totalAllStudents})</b></th>
                                        <th>Master Updated <br /><b>({totalStudents})</b></th>
                                        <th>Year</th>
                                        <th>Total Due <br /><b>({filteredTasks.filter(task => task.class !== "Consultant Pay" && task.class !== "REFUND").reduce((sum, task) => sum + (task.total_due || 0), 0).toLocaleString('en-IN')})</b></th>
                                        <th>Total Paid <br /><b>({filteredTasks.filter(task => task.class !== "Consultant Pay" && task.class !== "REFUND").reduce((sum, task) => sum + (task.total_ext_6 || 0), 0).toLocaleString('en-IN')})</b></th>
                                        <th>Total Master <br /><b>({filteredTasks.filter(task => task.class !== "Consultant Pay" && task.class !== "REFUND").reduce((sum, task) => sum + (task.total_master || 0), 0).toLocaleString('en-IN')})</b></th>
                                        <th className="text-danger">Due <br /><b>({totalDue > 0 ? Math.round(totalDue / totalMaster * 100).toLocaleString('en-IN') : '0'}%)</b></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedYears.map((year, yearIndex) => {
                                        const yearData = grouped[year];
                                        const regularData = yearData.filter(task => task.class !== "Consultant Pay" && task.class !== "REFUND");
                                        
                                        // Calculate year totals
                                        const yearTotalAllStudents = regularData.reduce((sum, task) => sum + (task.all_students || 0), 0);
                                        const yearTotalStudents = regularData.reduce((sum, task) => sum + (task.total_students || 0), 0);
                                        const yearTotalDue = regularData.reduce((sum, task) => sum + (task.total_due || 0), 0);
                                        const yearTotalPaid = regularData.reduce((sum, task) => sum + (task.total_ext_6 || 0), 0);
                                        const yearTotalMaster = regularData.reduce((sum, task) => sum + (task.total_master || 0), 0);
                                        const yearDuePercentage = yearTotalMaster > 0 ? Math.round(yearTotalDue / yearTotalMaster * 100) : 0;
                                        
                                        return (
                                            <React.Fragment key={year}>
                                                {/* Year Header Row */}
                                                <tr className="table-info">
                                                    <td colSpan="10" style={{textAlign: 'center', fontWeight: 'bold', fontSize: '16px'}}>
                                                        📅 Academic Year: {year}
                                                    </td>
                                                </tr>
                                                
                                                {/* Regular Classes for this year */}
                                                {regularData.map((taskuser, index) => (
                                                    <tr key={taskuser.id} className={yearIndex % 2 === 0 ? 'table-light' : ''}>
                                                        <td>{yearIndex * 100 + index + 1}</td>
                                                        <td>{taskuser.class}</td>
                                                        <td>{taskuser.dev}</td>
                                                        <td><b>{taskuser.all_students || 0}</b></td>
                                                        <td><b>{taskuser.total_students || 0}</b></td>
                                                        <td><b>{taskuser.year}</b></td>
                                                        <td><b>{taskuser.class !== "Consultant Pay" && taskuser.class !== "REFUND" && taskuser.total_due ? taskuser.total_due.toLocaleString('en-IN') : '0'}</b></td>
                                                        <td><b>{taskuser.class !== "Consultant Pay" && taskuser.class !== "REFUND" && taskuser.total_ext_6 ? taskuser.total_ext_6.toLocaleString('en-IN') : '0'}</b></td>
                                                        <td><b>{taskuser.class !== "Consultant Pay" && taskuser.class !== "REFUND" && taskuser.total_master ? taskuser.total_master.toLocaleString('en-IN') : '0'}</b></td>
                                                        <td>{taskuser.class !== "Consultant Pay" && taskuser.class !== "REFUND" && taskuser.total_master ? Math.round(taskuser.total_due / taskuser.total_master * 100) : 0}%</td>
                                                    </tr>
                                                ))}
                                                
                                                {/* Year Summary Row */}
                                                {regularData.length > 0 && (
                                                    <tr className="table-success" style={{fontWeight: 'bold', fontSize: '14px'}}>
                                                        <td style={{textAlign: 'left', backgroundColor: '#d4edda', color: '#155724'}}>
                                                            <span style={{fontSize: '16px'}}>🎯</span>
                                                        </td>
                                                        <td style={{textAlign: 'left', backgroundColor: '#d4edda', color: '#155724'}}>
                                                            <span style={{fontSize: '16px'}}>{year}</span>
                                                        </td>
                                                        <td style={{textAlign: 'left', backgroundColor: '#d4edda', color: '#155724'}}>
                                                            <span style={{fontSize: '16px'}}>YEAR TOTAL</span>
                                                        </td>
                                                        <td style={{textAlign: 'left', backgroundColor: '#d4edda', color: '#155724'}}>
                                                            <span style={{fontSize: '16px'}}>{yearTotalAllStudents}</span>
                                                        </td>
                                                        <td style={{textAlign: 'left', backgroundColor: '#d4edda', color: '#155724'}}>
                                                            <span style={{fontSize: '16px'}}>{yearTotalStudents}</span>
                                                        </td>
                                                        <td style={{textAlign: 'left', backgroundColor: '#d4edda', color: '#155724'}}>
                                                            <span style={{fontSize: '16px'}}>{year}</span>
                                                        </td>
                                                        <td style={{textAlign: 'left', backgroundColor: '#d4edda', color: '#155724'}}>
                                                            <span style={{fontSize: '16px'}}>₹{yearTotalDue.toLocaleString('en-IN')}</span>
                                                        </td>
                                                        <td style={{textAlign: 'left', backgroundColor: '#d4edda', color: '#155724'}}>
                                                            <span style={{fontSize: '16px'}}>₹{yearTotalPaid.toLocaleString('en-IN')}</span>
                                                        </td>
                                                        <td style={{textAlign: 'left', backgroundColor: '#d4edda', color: '#155724'}}>
                                                            <span style={{fontSize: '16px'}}>₹{yearTotalMaster.toLocaleString('en-IN')}</span>
                                                        </td>
                                                        <td style={{textAlign: 'left', backgroundColor: '#d4edda', color: '#155724'}}>
                                                            <span style={{fontSize: '16px', fontWeight: 'bold'}}>{yearDuePercentage}%</span>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>

                            {/* Separate table for Consultant Pay */}
                            {filteredTasks.filter(taskuser => taskuser.class === "Consultant Pay").length > 0 && (
                                <>
                                    <h3>Consultant Pay</h3>
                                    <table className="table table-striped">
                                        <thead className="table-primary">
                                            <tr>
                                                <th className="text-danger">No</th>
                                                <th>Class</th>
                                                <th>Division</th>
                                                <th>All Students</th>
                                                <th>Total Students</th>
                                                <th>Year</th>
                                                <th>Total Due</th>
                                                <th>Total Paid</th>
                                                <th>Total Master</th>
                                                <th>Due</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedYears.map((year, yearIndex) => {
                                                const yearData = grouped[year];
                                                const consultantData = yearData.filter(task => task.class === "Consultant Pay");
                                                
                                                return (
                                                    <React.Fragment key={year}>
                                                        {/* Year Header Row for Consultant */}
                                                        {consultantData.length > 0 && (
                                                            <tr className="table-warning">
                                                                <td colSpan="10" style={{textAlign: 'center', fontWeight: 'bold', fontSize: '16px'}}>
                                                                    📅 Academic Year: {year} - Consultant Pay
                                                                </td>
                                                            </tr>
                                                        )}
                                                        
                                                        {consultantData.map((taskuser, index) => (
                                                            <tr key={taskuser.id} className={yearIndex % 2 === 0 ? 'table-light' : ''}>
                                                                <td>{yearIndex * 100 + index + 1}</td>
                                                                <td>{taskuser.class}</td>
                                                                <td>{taskuser.dev}</td>
                                                                <td><b>{taskuser.all_students || 0}</b></td>
                                                                <td><b>{taskuser.total_students || 0}</b></td>
                                                                <td><b>{taskuser.year}</b></td>
                                                                <td><b>{taskuser.total_due ? taskuser.total_due.toLocaleString('en-IN') : '0'}</b></td>
                                                                <td><b>{taskuser.total_ext_6 ? taskuser.total_ext_6.toLocaleString('en-IN') : '0'}</b></td>
                                                                <td><b>{taskuser.total_master ? taskuser.total_master.toLocaleString('en-IN') : '0'}</b></td>
                                                                <td>{taskuser.total_master ? Math.round(taskuser.total_due / taskuser.total_master * 100) : 0}%</td>
                                                            </tr>
                                                        ))}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </>
                            )}

                            {/* Separate table for REFUND */}
                            {filteredTasks.filter(taskuser => taskuser.class === "REFUND").length > 0 && (
                                <>
                                    <h3 className="mt-4">REFUND</h3>
                                    <table className="table table-striped">
                                        <thead className="table-danger">
                                            <tr>
                                                <th className="text-danger">No</th>
                                                <th>Class</th>
                                                <th>Division</th>
                                                <th>All Students</th>
                                                <th>Total Students</th>
                                                <th>Year</th>
                                                <th>Total Due</th>
                                                <th>Total Paid</th>
                                                <th>Total Master</th>
                                                <th>Due</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedYears.map((year, yearIndex) => {
                                                const yearData = grouped[year];
                                                const refundData = yearData.filter(task => task.class === "REFUND");
                                                
                                                return (
                                                    <React.Fragment key={year}>
                                                        {/* Year Header Row for REFUND */}
                                                        {refundData.length > 0 && (
                                                            <tr className="table-danger">
                                                                <td colSpan="10" style={{textAlign: 'center', fontWeight: 'bold', fontSize: '16px'}}>
                                                                    📅 Academic Year: {year} - REFUND
                                                                </td>
                                                            </tr>
                                                        )}
                                                        
                                                        {refundData.map((taskuser, index) => (
                                                            <tr key={taskuser.id} className={yearIndex % 2 === 0 ? 'table-light' : ''}>
                                                                <td>{yearIndex * 100 + index + 1}</td>
                                                                <td>{taskuser.class}</td>
                                                                <td>{taskuser.dev}</td>
                                                                <td><b>{taskuser.all_students || 0}</b></td>
                                                                <td><b>{taskuser.total_students || 0}</b></td>
                                                                <td><b>{taskuser.year}</b></td>
                                                                <td><b>{taskuser.total_due ? taskuser.total_due.toLocaleString('en-IN') : '0'}</b></td>
                                                                <td><b>{taskuser.total_ext_6 ? taskuser.total_ext_6.toLocaleString('en-IN') : '0'}</b></td>
                                                                <td><b>{taskuser.total_master ? taskuser.total_master.toLocaleString('en-IN') : '0'}</b></td>
                                                                <td>{taskuser.total_master ? Math.round(taskuser.total_due / taskuser.total_master * 100) : 0}%</td>
                                                            </tr>
                                                        ))}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
}
